import { ChevronDown } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from 'src/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AssetReportContainer } from 'src/containers/AssetReport';
import { Button } from '@/components/ui/button';
import { useSoftwareEscrow } from 'src/context/software-escrow';

import type { IAssetReportGroupsQuery } from 'src/graphql/actions/reports/assetReportGroups.query';

type IAssetReportRow = {
  id: string;
  depositSource: string;
  period: string;
  createdAt: string;
};

const ReportView = ({ reportId }: { reportId: string }) => {
  const {
    softwareEscrow: { id: softwareEscrowId, policy },
  } = useSoftwareEscrow();

  return (
    <AssetReportContainer
      escrowId={softwareEscrowId}
      canDownload={policy.write}
      reportId={reportId}
      triggerNode={
        <Button variant="outline" size="sm">
          View
        </Button>
      }
    />
  );
};

const columnHelper = createColumnHelper<IAssetReportRow>();
const columns = [
  columnHelper.accessor('depositSource', {
    header: 'Name',
    cell: (props) => props.getValue(),
  }),
  columnHelper.accessor('period', {
    header: 'Report version',
    cell: (props) => dayjs(props.getValue()).format('MMM YYYY'),
  }),
  columnHelper.accessor('createdAt', {
    header: 'Date generated',
    cell: (props) => {
      const date = dayjs(props.getValue()).format('MM/DD/YYYY');
      const time = dayjs(props.getValue()).format('HH:mm A');

      return (
        <Fragment>
          <div className="text-sm font-medium">{date}</div>
          <div className="text-xs text-muted-foreground">{time}</div>
        </Fragment>
      );
    },
  }),
  columnHelper.accessor('id', {
    header: '',
    cell: (props) => <ReportView reportId={props.getValue()} />,
    meta: {
      headerClassName: 'w-[90px]',
      cellClassName: 'w-[90px]',
    },
  }),
];

const AssetReport = ({
  assetReport,
}: {
  assetReport: IAssetReportGroupsQuery['assetReportGroups']['nodes'][number];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const data = useMemo(
    () =>
      assetReport.assetReports.map((report) => ({
        id: report.id,
        depositSource: report.asset.depositSource ?? '',
        period: report.period,
        createdAt: report.createdAt,
      })),
    [assetReport.assetReports],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CardHeader className="flex-row justify-between items-center gap-1 space-y-0 p-4 sm:p-6">
          <div className="text-base font-semibold truncate">{assetReport.asset.depositSource}</div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs">
              {isOpen ? 'Hide' : 'Show'} report history
              <ChevronDown className={cn('transition-transform rotate-0', isOpen && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <div className="border border-border rounded-md">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'h-11 bg-gray-50 text-muted-foreground font-bold text-xs whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                            header.column.columnDef.meta?.headerClassName,
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className={cn(cell.column.columnDef.meta?.cellClassName)}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default AssetReport;
